import { useDispatch, useProject } from '@witmetrics/data-store';
import { API } from '@/api';
import { showSuccessToaster } from '@/utils/toasters';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { TrashIcon } from '@/icons';
import DefaultDialog from '@/components/Dialog/DefaultDialog';

export type DeleteProjectDialogProps = {
  projectId: number;
  onDelete?: (projectId: number) => void;
  onClose: () => void;
};

export default function DeleteProjectDialog({
  projectId,
  onDelete,
  onClose,
}: DeleteProjectDialogProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const project = useProject(projectId);
  const [isDeleting, toggleDeleting] = useToggle();

  const handleDelete = async () => {
    try {
      toggleDeleting(true);
      await API.UnisonProjects.removeUnisonProject(projectId);
      dispatch.deleteProject({ projectId });
      onDelete?.(projectId);
      showSuccessToaster('Case deleted');
      toggleDeleting(false);
      onClose();
    } catch (err) {
      toggleDeleting(false);
      onApiError(err, 'Error deleting case', () => {
        handleDelete();
      });
    }
  };

  return (
    <DefaultDialog
      disabled={isDeleting}
      pending={isDeleting}
      variant="confirm"
      icon={<TrashIcon size="1.375rem" />}
      confirmLabel="Delete"
      pendingLabel="Deleting"
      title="Delete case"
      message={
        <>
          {`Are you sure you want to remove "${project!.name}"?`} All data
          associated with this case will be deleted. This action cannot be
          undone.
        </>
      }
      onCancel={onClose}
      onConfirm={handleDelete}
      onClose={onClose}
    />
  );
}
